// JSON-path loose-FK snippet builder (Phase 5), extracted from
// looseFkJoinSnippet.ts to keep both files within the repo's 200-line hard
// cap. Behaviour is identical to the inlined version.
//
// JSON-path loose rels (`sourcePath` set) cannot be a plain `FkEdge` (the
// source side is a JSON-extract expression, not a bare column), so they get
// a dedicated snippet whose ON clause emits the engine-specific
// `extractTextSql` of the alias-qualified source column at the path.
//
// `extractTextSql` lives in the renderer util `jsonFilterSql.ts` (types-only
// imports — renderer-safe, no electron dependency) and is the single source
// of truth for per-engine JSON-extract SQL, shared with the Table Explorer
// query builder.

import type {
  AliasBinding,
  CompletionCandidate,
  DatabaseEngineId,
  TableMeta,
} from '../../../core/engines';
import type { LooseRelationship } from '@/types/electron';
import { extractTextSql } from '@/features/workspace/common/utils/jsonFilterSql';
import { databaseEngineIdToSqlEngine } from '../../../core/engines/databaseEngineIdToSqlEngine';
import { declaredCatalogSpelling } from '../../sql-resolver/suggest/sqlIdentifierText';

import { boundAliasSql, catalogIdentWriter, catalogTableSql, generateShortAlias, schemaMatches } from './fkJoinSnippet';
import type { FkEndpointMatcher } from './fkEndpointMatch';

type SideColumns = readonly { readonly name: string }[] | undefined;

type JsonPathSnippetInput = {
  readonly binding: AliasBinding;
  readonly candidate: TableMeta;
  readonly rel: LooseRelationship;
  readonly existingAliases: ReadonlySet<string>;
  readonly engine: DatabaseEngineId;
  /** Relation identity (`fkEndpointMatch.ts`); without it, folded names, missing schema = any. */
  readonly match?: FkEndpointMatcher;
  /** Each side's columns, so the declared columns are written in their catalog spelling. */
  readonly columns?: { readonly candidate?: SideColumns; readonly bound?: SideColumns };
};

/**
 * Build the JSON-path loose-FK snippet. The source side of the ON clause is
 * the engine-specific JSON-extract of the alias-qualified source column at
 * `rel.sourcePath` (so the alias lands *inside* the extract:
 * `JSON_VALUE(o.metadata, '$.customer_id')`). Matches `generateFkJoinSnippet`
 * candidate shape (tier-1 fkJoin snippet) so the ranker treats it identically.
 *
 * Returns `null` when the loose rel does not connect the bound relation to
 * the candidate in either direction — same contract as `findApplicableFks`.
 */
export function generateLooseJsonPathJoinSnippet(
  input: JsonPathSnippetInput,
): CompletionCandidate | null {
  const { binding, candidate, rel, existingAliases, engine, match, columns } = input;
  const side = looseJsonPathBoundSide(binding, candidate, rel, match);
  if (!side) return null;
  const newAlias = generateShortAlias(candidate.name, existingAliases);
  // The candidate table is a catalog spelling; each declared column is written
  // in the catalog spelling of its own side's table (`declaredCatalogSpelling`).
  const ident = catalogIdentWriter(engine);
  const sqlEngine = databaseEngineIdToSqlEngine(engine);
  const spell = (declared: string, sideColumns: SideColumns): string =>
    sqlEngine ? declaredCatalogSpelling(declared, sideColumns?.map((c) => c.name)) : declared;
  const sourceColumns = side === 'source' ? columns?.bound : columns?.candidate;
  const targetColumns = side === 'source' ? columns?.candidate : columns?.bound;
  const fromColumn = ident(spell(rel.fromColumn, sourceColumns));
  const toColumn = ident(spell(rel.toColumn, targetColumns));
  const relation = catalogTableSql(candidate, engine);
  // The source column (JSON) is always on `rel.fromTable`; the scalar target
  // column on `rel.toTable`.
  if (side === 'source') {
    // Bound relation is the JSON/source side; we JOIN to the target.
    const sourceExpr = extractTextSql(engine, `${boundAliasSql(binding)}.${fromColumn}`, rel.sourcePath!);
    return buildCandidate(candidate, relation, newAlias, `\${1}.${toColumn} = ${sourceExpr}`, rel);
  }
  // Bound relation is the scalar/target side; we JOIN to the JSON source.
  const sourceExpr = extractTextSql(engine, `\${1}.${fromColumn}`, rel.sourcePath!);
  return buildCandidate(candidate, relation, newAlias, `${sourceExpr} = ${boundAliasSql(binding)}.${toColumn}`, rel);
}

/**
 * Which side of `rel` the bound relation sits on when `candidate` is joined to
 * it — `'source'` (it holds the JSON column) is tested first — by relation
 * identity, schemas included: `a.docs → b.docs` matches both ways by name
 * alone. `null` when the rel does not connect the two. The snippet's ON and
 * its loose-FK filters both follow this answer. With `match` the bound
 * relation and the declared tables resolve to snapshot tables
 * (`fkEndpointMatch.ts`).
 */
export function looseJsonPathBoundSide(
  binding: AliasBinding,
  candidate: TableMeta,
  rel: LooseRelationship,
  match?: FkEndpointMatcher,
): 'source' | 'target' | null {
  if (match) {
    const bound = match.bindingTable(binding);
    if (!bound) return null;
    const from = (t: { name: string; schema?: string }) => match.endpointIs(rel.fromSchema || undefined, rel.fromTable, t);
    const to = (t: { name: string; schema?: string }) => match.endpointIs(rel.toSchema || undefined, rel.toTable, t);
    if (from(bound) && to(candidate)) return 'source';
    if (to(bound) && from(candidate)) return 'target';
    return null;
  }
  const fromName = binding.relation.toLowerCase();
  const candName = candidate.name.toLowerCase();
  const relFrom = rel.fromTable.toLowerCase();
  const relTo = rel.toTable.toLowerCase();
  // Loose rels store `''` for schemaless engines; normalize to `undefined`
  // so `schemaMatches` applies its "any" rule (mirrors `looseRelToFkEdge`,
  // which omits empty schemas before they reach `findApplicableFks`). Without
  // this, same-named tables in different schemas get a wrong JOIN suggestion.
  const relFromSchema = rel.fromSchema || undefined;
  const relToSchema = rel.toSchema || undefined;
  if (
    relFrom === fromName &&
    schemaMatches(relFromSchema, binding.schema) &&
    relTo === candName &&
    schemaMatches(relToSchema, candidate.schema)
  ) {
    return 'source';
  }
  if (
    relTo === fromName &&
    schemaMatches(relToSchema, binding.schema) &&
    relFrom === candName &&
    schemaMatches(relFromSchema, candidate.schema)
  ) {
    return 'target';
  }
  return null;
}

/** A loose relationship's completion detail — never the relationship's internal id. */
export function looseFkDetail(rel: LooseRelationship): string {
  return `Loose FK: ${rel.fromColumn}${rel.sourcePath ? ` ${rel.sourcePath}` : ''} → ${rel.toTable}.${rel.toColumn}`;
}

function buildCandidate(
  candidate: TableMeta,
  relation: string,
  newAlias: string,
  onClause: string,
  rel: LooseRelationship,
): CompletionCandidate {
  const insertText = `${relation} \${1:${newAlias}} ON ${onClause}$0`;
  const label = `${relation} ON ${onClause.replace(/\$\{1\}/g, newAlias)}`;
  return {
    label,
    kind: 'snippet',
    tier: 1,
    insertText,
    isSnippet: true,
    detail: looseFkDetail(rel),
    filterText: candidate.name,
    fkJoin: true,
  };
}
